import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ExecuteStepInput } from 'sr-types/lib/workflow/v1/graphql';
import { UserContext } from '../../auth/UserContext';
import { FormProvider } from '../../form/FormContext';
import Validator, { ValidatorType } from '../../form/Validator';
import { failedToastMessage, successToastMessage } from '../../utils/commonUtils';
import { ApprovalActionInputInvalidError } from '../constants';
import { useExecuteApprovalActionApi } from '../data/useExecuteApprovalActionApi';
import { useFetchApprovalApi } from '../data/useFetchApprovalApi';
import { ReviewApprovalContextValue } from '../types';
import ApprovalActionInputValidityObserver from './components/ApprovalActionInputValidityObserver';
import { ReviewApprovalContext } from './components/ReviewApprovalContext';
import { getReviewApprovalDerivedState } from './utils';

const approvalActionInputValidationRules: Record<
    Extract<keyof ExecuteStepInput, 'stepId' | 'action'>,
    Array<ValidatorType>
> = {
    stepId: [Validator.RULES.isRequired],
    action: [
        {
            isValid: (name, formState) => Boolean(formState?.[name]?.id),
            messageKey: 'approval.action.input.invalid.message'
        }
    ]
};

export type ReviewApprovalProviderProps = PropsWithChildren<{
    /**
     * The unique identifier of the approval request to be reviewed.
     */
    approvalId: string;

    /**
     * Optional flag to indicate whether the data operation function exposed by this context provider
     * should handle success/error scenarios and show basic UI notifications,
     * or return result / throw errors to be handled by the caller.
     *
     * @default true
     */
    throwOnError?: boolean;
}>;

export default function ReviewApprovalProvider({
    approvalId,
    children,
    throwOnError = true
}: ReviewApprovalProviderProps) {
    const { userProfile } = useContext(UserContext);

    const [approvalActionInput, setApprovalActionInput] = useState<ExecuteStepInput | null>(null);
    const [isApprovalActionInputValid, setIsApprovalActionInputValid] = useState<boolean>(false);

    const {
        isFetchingApprovalData,
        errorFetchingApprovalData,
        approvalData,
        refetchApprovalData,
        approvalAttachmentsData,
        isFetchingApprovalAttachmentsData,
        errorFetchingApprovalAttachmentsData,
        refetchAttachmentsData
    } = useFetchApprovalApi(approvalId);

    const { userStep, isUserAllowedToViewApprovalActions, isUserAllowedToExecuteApprovalActions } = useMemo(() => {
        return getReviewApprovalDerivedState(approvalData, userProfile);
    }, [approvalData, userProfile]);

    /**
     * Auto-set the stepId in the approval action input
     * - if the user is one of the approvers
     * - and the user is allowed to execute actions
     */
    useEffect(() => {
        if (userStep && isUserAllowedToExecuteApprovalActions) {
            setApprovalActionInput((prevData) => {
                if (prevData?.stepId?.label === userStep.idNamespace && prevData?.stepId?.value === userStep.id) {
                    return prevData;
                }

                return {
                    ...(prevData ?? ({} as ExecuteStepInput)),
                    stepId: {
                        label: userStep.idNamespace,
                        value: userStep.id
                    }
                };
            });
        }
    }, [userStep, isUserAllowedToExecuteApprovalActions]);

    const { executeApprovalAction: executeApprovalActionOnDataStore, isExecutingApprovalAction } =
        useExecuteApprovalActionApi();

    const executeApprovalAction = useCallback<ReviewApprovalContextValue['executeApprovalAction']>(async () => {
        let result: Awaited<ReturnType<typeof executeApprovalActionOnDataStore>> = null;

        const isInputValid = approvalActionInput && isApprovalActionInputValid;

        if (throwOnError) {
            if (!isInputValid) {
                throw new ApprovalActionInputInvalidError();
            }

            result = await executeApprovalActionOnDataStore(approvalId, approvalActionInput);
        } else {
            if (!isInputValid) {
                failedToastMessage('approval.action.input.invalid.message');
                return null;
            }

            try {
                result = await executeApprovalActionOnDataStore(approvalId, approvalActionInput);
                successToastMessage('approval.action.execution.success.message');
            } catch (error) {
                failedToastMessage('approval.action.execution.failed.message');
                return null;
            }
        }

        setApprovalActionInput(null);
        setIsApprovalActionInputValid(false);
        await refetchApprovalData({ errorPolicy: 'all' });
        return result;
    }, [
        approvalActionInput,
        approvalId,
        executeApprovalActionOnDataStore,
        isApprovalActionInputValid,
        refetchApprovalData,
        throwOnError
    ]);

    const contextValue = useMemo(() => {
        const contextValue: ReviewApprovalContextValue = {
            // Provider props
            approvalId,
            throwOnError,
            // Approval entity data
            approvalData,
            isFetchingApprovalData,
            errorFetchingApprovalData,
            refetchApprovalData,
            // Approval attachments data
            approvalAttachmentsData,
            isFetchingApprovalAttachmentsData,
            errorFetchingApprovalAttachmentsData,
            refetchAttachmentsData,
            // Approval action input
            approvalActionInput,
            isApprovalActionInputValid,
            executeApprovalAction,
            isExecutingApprovalAction,
            // Derived state
            userStep,
            isUserAllowedToViewApprovalActions,
            isUserAllowedToExecuteApprovalActions
        };

        return contextValue;
    }, [
        approvalActionInput,
        approvalAttachmentsData,
        approvalData,
        approvalId,
        errorFetchingApprovalAttachmentsData,
        errorFetchingApprovalData,
        executeApprovalAction,
        isApprovalActionInputValid,
        isExecutingApprovalAction,
        isFetchingApprovalAttachmentsData,
        isFetchingApprovalData,
        isUserAllowedToExecuteApprovalActions,
        isUserAllowedToViewApprovalActions,
        refetchApprovalData,
        refetchAttachmentsData,
        throwOnError,
        userStep
    ]);

    return (
        <ReviewApprovalContext value={contextValue}>
            <FormProvider
                state={approvalActionInput}
                setState={setApprovalActionInput}
                validationRules={approvalActionInputValidationRules}
            >
                {children}
                <ApprovalActionInputValidityObserver
                    onApprovalActionDataValidityChange={setIsApprovalActionInputValid}
                />
            </FormProvider>
        </ReviewApprovalContext>
    );
}
